/**
 * 基于 Web Audio API (AudioContext) 的音效管理器
 * 相比 HTML5 Audio，AudioContext 能更好地绕过浏览器的自动播放限制
 * 并且性能更好，支持音频处理
 */
import { AudioManager } from './audio-manager';
import type { PlayOptions, AudioConfigMap } from './types';
import { getPublicUrl } from '@/utils/cdn';
import { getAudioContextClass } from './audio-utils';

interface AudioResource {
  id: string;
  path: string;
  buffer?: AudioBuffer;
  loaded: boolean;
}

interface LoopingSound {
  source: AudioBufferSourceNode;
  gainNode: GainNode;
  uniqueKey: string;
}

/**
 * 全局预加载状态管理
 * 记录已经预加载成功的路径，避免多个实例重复预加载
 */
const globalPreloadCache = {
  // 已预加载成功的路径及其 AudioBuffer
  loadedBuffers: new Map<string, AudioBuffer>(),
  // 正在预加载中的路径及其 Promise
  loadingPromises: new Map<string, Promise<AudioBuffer | null>>(),
};

export class SoundEffectManagerWebAudio {
  private audioManager: AudioManager;
  private audioContext: AudioContext | null = null;
  private resources: Map<string, AudioResource> = new Map();
  private loopingSounds: Map<string, LoopingSound> = new Map();

  constructor(audioManager: AudioManager) {
    this.audioManager = audioManager;
  }

  /**
   * 初始化 AudioContext（延迟初始化，在用户交互后）
   */
  private initAudioContext(): AudioContext {
    if (!this.audioContext) {
      const AudioContextClass = getAudioContextClass();

      if (!AudioContextClass) {
        throw new Error('AudioContext is not supported in this browser');
      }

      this.audioContext = new AudioContextClass();
    }

    // 如果 AudioContext 是挂起状态，尝试恢复
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume().catch((error) => {
        console.warn('Failed to resume AudioContext:', error);
      });
    }

    return this.audioContext;
  }

  /**
   * 获取 AudioContext（如果未初始化则创建）
   */
  private getAudioContext(): AudioContext | null {
    try {
      return this.initAudioContext();
    } catch (error) {
      console.error('Failed to get AudioContext:', error);
      return null;
    }
  }

  /**
   * 注册音频资源
   */
  register(id: string, path: string): void {
    this.resources.set(id, {
      id,
      path: getPublicUrl(path),
      loaded: false,
    });
  }

  /**
   * 批量注册音频资源
   */
  registerAll(configMap: AudioConfigMap): void {
    Object.entries(configMap).forEach(([id, path]) => {
      this.register(id, path);
    });
  }

  /**
   * 检查资源是否已注册
   */
  isRegistered(id: string): boolean {
    return this.resources.has(id);
  }

  /**
   * 取消注册/卸载音频资源
   */
  unregister(id: string): void {
    const resource = this.resources.get(id);
    if (resource) {
      resource.buffer = undefined;
      resource.loaded = false;
    }
    this.resources.delete(id);
  }

  /**
   * 使用 fetch 加载音频文件并解码为 AudioBuffer
   */
  private async fetchAndDecodeAudio(path: string): Promise<AudioBuffer> {
    const audioContext = this.getAudioContext();
    if (!audioContext) {
      throw new Error('AudioContext is not available');
    }

    // 使用 fetch 加载音频数据
    const response = await fetch(path);
    if (!response.ok) {
      throw new Error(`Failed to fetch audio: ${response.statusText}`);
    }

    const arrayBuffer = await response.arrayBuffer();

    // 解码音频数据为 AudioBuffer
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    return audioBuffer;
  }

  /**
   * 预加载音频
   * 使用全局缓存避免多个实例重复预加载同一资源
   */
  async preload(id: string): Promise<void> {
    const resource = this.resources.get(id);
    if (!resource) {
      console.warn(`Sound effect resource "${id}" is not registered`);
      return;
    }

    // 检查实例级别的预加载状态
    if (resource.loaded && resource.buffer) {
      return;
    }

    const path = resource.path;

    // 检查全局预加载状态
    const cachedBuffer = globalPreloadCache.loadedBuffers.get(path);
    if (cachedBuffer) {
      resource.buffer = cachedBuffer;
      resource.loaded = true;
      return;
    }

    // 如果正在预加载中，等待现有的 Promise
    const existingPromise = globalPreloadCache.loadingPromises.get(path);
    if (existingPromise) {
      const buffer = await existingPromise;
      if (buffer) {
        resource.buffer = buffer;
        resource.loaded = true;
      }
      return;
    }

    // 创建新的预加载 Promise
    const preloadPromise = this.fetchAndDecodeAudio(path)
      .then((buffer) => {
        // 更新全局缓存
        globalPreloadCache.loadedBuffers.set(path, buffer);
        globalPreloadCache.loadingPromises.delete(path);

        // 更新实例状态
        resource.buffer = buffer;
        resource.loaded = true;

        return buffer;
      })
      .catch((error) => {
        console.warn(`Failed to preload sound effect "${id}":`, error);
        globalPreloadCache.loadingPromises.delete(path);
        return null;
      });

    // 记录正在预加载的 Promise
    globalPreloadCache.loadingPromises.set(path, preloadPromise);

    await preloadPromise;
  }

  /**
   * 批量预加载音频
   */
  async preloadAll(ids?: string[]): Promise<Array<{ id: string; success: boolean; error?: Error; }>> {
    const targetIds = ids ?? Array.from(this.resources.keys());

    const preloadPromises = targetIds.map(async (id) => {
      const resource = this.resources.get(id);
      if (!resource) {
        return {
          id,
          success: false,
          error: new Error(`Sound effect resource "${id}" is not registered`),
        };
      }

      await this.preload(id);

      if (resource.loaded && resource.buffer) {
        return { id, success: true };
      } else {
        return {
          id,
          success: false,
          error: new Error(`Failed to preload sound effect "${id}"`),
        };
      }
    });

    return Promise.all(preloadPromises);
  }

  /**
   * 创建音频源节点
   */
  private createSourceNode(buffer: AudioBuffer, loop: boolean = false): {
    source: AudioBufferSourceNode;
    gainNode: GainNode;
  } {
    const audioContext = this.getAudioContext();
    if (!audioContext) {
      throw new Error('AudioContext is not available');
    }

    // 创建音频源节点
    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    source.loop = loop;

    // 创建增益节点（用于音量控制）
    const gainNode = audioContext.createGain();

    // 连接节点：source → gainNode → destination
    source.connect(gainNode);
    gainNode.connect(audioContext.destination);

    return { source, gainNode };
  }

  /**
   * 播放音效（单次）
   */
  async playSound(id: string, options: PlayOptions = {}): Promise<void> {
    // 检查全局音效开关
    if (!this.audioManager.getSoundEffectEnabled()) {
      return;
    }

    try {
      const resource = this.resources.get(id);
      if (!resource) {
        throw new Error(`Sound effect resource "${id}" is not registered`);
      }

      // 如果还没有预加载，先预加载
      if (!resource.loaded || !resource.buffer) {
        await this.preload(id);
      }

      if (!resource.buffer) {
        throw new Error(`Sound effect "${id}" failed to load`);
      }

      const audioContext = this.getAudioContext();
      if (!audioContext) {
        throw new Error('AudioContext is not available');
      }

      // 创建音频源节点
      const { source, gainNode } = this.createSourceNode(resource.buffer, options.loop ?? false);

      // 设置音量
      const volume = this.audioManager.calculateSoundEffectVolume(1.0, options.volume ?? 1.0);
      gainNode.gain.value = volume;

      // 添加结束回调
      if (options.onEnded) {
        source.addEventListener('ended', options.onEnded, { once: true });
      }

      // 播放
      source.start(0);

      // 如果不是循环播放，播放结束后清理资源
      if (!options.loop) {
        source.addEventListener('ended', () => {
          try {
            source.disconnect();
            gainNode.disconnect();
          // eslint-disable-next-line unused-imports/no-unused-vars
          } catch (e) {
            // 忽略断开连接时的错误
          }
        }, { once: true });
      }
    } catch (error) {
      console.error(`Failed to play sound effect "${id}":`, error);
      options.onError?.(error as Error);
    }
  }

  /**
   * 循环播放音效
   */
  async playLoopingSound(id: string, uniqueKey: string, options: PlayOptions = {}): Promise<void> {
    // 检查全局音效开关
    if (!this.audioManager.getSoundEffectEnabled()) {
      return;
    }

    // 如果已经存在相同 key 的循环音效，先停止
    if (this.loopingSounds.has(uniqueKey)) {
      this.stopLoopingSound(uniqueKey);
    }

    try {
      const resource = this.resources.get(id);
      if (!resource) {
        throw new Error(`Sound effect resource "${id}" is not registered`);
      }

      // 如果还没有预加载，先预加载
      if (!resource.loaded || !resource.buffer) {
        await this.preload(id);
      }

      if (!resource.buffer) {
        throw new Error(`Sound effect "${id}" failed to load`);
      }

      const audioContext = this.getAudioContext();
      if (!audioContext) {
        throw new Error('AudioContext is not available');
      }

      // 创建循环音频源节点
      const { source, gainNode } = this.createSourceNode(resource.buffer, true);

      // 设置音量
      const volume = this.audioManager.calculateSoundEffectVolume(1.0, options.volume ?? 1.0);
      gainNode.gain.value = volume;

      // 播放
      source.start(0);

      // 保存循环音效的引用
      this.loopingSounds.set(uniqueKey, { source, gainNode, uniqueKey });
    } catch (error) {
      console.error(`Failed to play looping sound effect "${id}":`, error);
      options.onError?.(error as Error);
    }
  }

  /**
   * 快速切换循环音效
   * 先启动新音效，再停止旧音效，减少切换间隙
   */
  async switchLoopingSound(newId: string, uniqueKey: string, options: PlayOptions = {}): Promise<void> {
    // 检查全局音效开关
    if (!this.audioManager.getSoundEffectEnabled()) {
      return;
    }

    const currentSound = this.loopingSounds.get(uniqueKey);

    // 如果当前有相同 key 的音效在播放，且是同一个音效，直接返回
    if (currentSound) {
      const resource = this.resources.get(newId);
      if (resource && resource.buffer && currentSound.source.buffer === resource.buffer) {
        return; // 已经是同一个音效，无需切换
      }
    }

    try {
      const resource = this.resources.get(newId);
      if (!resource) {
        throw new Error(`Sound effect resource "${newId}" is not registered`);
      }

      // 如果还没有预加载，先预加载
      if (!resource.loaded || !resource.buffer) {
        await this.preload(newId);
      }

      if (!resource.buffer) {
        throw new Error(`Sound effect "${newId}" failed to load`);
      }

      const audioContext = this.getAudioContext();
      if (!audioContext) {
        throw new Error('AudioContext is not available');
      }

      // 创建新的循环音频源节点
      const { source, gainNode } = this.createSourceNode(resource.buffer, true);

      // 设置音量
      const volume = this.audioManager.calculateSoundEffectVolume(1.0, options.volume ?? 1.0);
      gainNode.gain.value = volume;

      // 播放新音效
      source.start(0);

      // 停止旧音效（如果存在）
      if (currentSound) {
        try {
          currentSound.source.stop();
          currentSound.source.disconnect();
          currentSound.gainNode.disconnect();
        // eslint-disable-next-line unused-imports/no-unused-vars
        } catch (e) {
          // 忽略停止旧音效时的错误
        }
      }

      // 设置新音效
      this.loopingSounds.set(uniqueKey, { source, gainNode, uniqueKey });
    } catch (error) {
      console.error(`Failed to switch looping sound effect "${newId}":`, error);
      options.onError?.(error as Error);
    }
  }

  /**
   * 停止循环音效
   */
  stopLoopingSound(uniqueKey: string): void {
    const loopingSound = this.loopingSounds.get(uniqueKey);
    if (loopingSound) {
      try {
        loopingSound.source.stop();
        loopingSound.source.disconnect();
        loopingSound.gainNode.disconnect();
      // eslint-disable-next-line unused-imports/no-unused-vars
      } catch (e) {
        // 忽略停止时的错误（可能已经停止）
      }
      this.loopingSounds.delete(uniqueKey);
    }
  }

  /**
   * 停止所有音效
   */
  stopAllSounds(): void {
    this.loopingSounds.forEach((loopingSound) => {
      try {
        loopingSound.source.stop();
        loopingSound.source.disconnect();
        loopingSound.gainNode.disconnect();
      // eslint-disable-next-line unused-imports/no-unused-vars
      } catch (e) {
        // 忽略停止时的错误
      }
    });
    this.loopingSounds.clear();
  }

  /**
   * 更新所有循环音效音量（响应全局音量变化）
   */
  updateVolume(): void {
    const volume = this.audioManager.calculateSoundEffectVolume(1.0, 1.0);

    this.loopingSounds.forEach((loopingSound) => {
      loopingSound.gainNode.gain.value = volume;
    });

    // 如果音效被关闭，停止所有循环音效
    if (!this.audioManager.getSoundEffectEnabled()) {
      this.stopAllSounds();
    }
  }

  /**
   * 解锁音频上下文（在用户交互时调用）
   * 这是 Safari 等浏览器要求的
   */
  async unlockAudioContext(): Promise<boolean> {
    try {
      const audioContext = this.initAudioContext();

      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      // 播放一个静音缓冲区以完全解锁
      const buffer = audioContext.createBuffer(1, 1, 22050);
      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContext.destination);
      source.start(0);

      return true;
    } catch (error) {
      console.error('Failed to unlock audio context:', error);
      return false;
    }
  }

  /**
   * 清理资源（销毁时调用）
   */
  destroy(): void {
    this.stopAllSounds();

    if (this.audioContext) {
      this.audioContext.close().catch((error) => {
        console.warn('Failed to close AudioContext:', error);
      });
      this.audioContext = null;
    }

    this.resources.clear();
  }
}
